/**
 * 任意录音 blob → 16kHz 单声道 WAV。
 *
 * 为什么必须转 WAV（而语音输入那条链路不用）：
 *   - /api/asr 那边服务端有 ffmpeg 兜底，直接吃 opus，所以那条路径省流量不转
 *   - 音色注册这条不行：门户用 Python 的 wave 模块读时长做校验，fire 再用
 *     soundfile 读一遍提特征，两边都只认 WAV
 *
 * 为什么降到 16k 单声道：CosyVoice 提 prompt 特征时本来就重采样到 16k，
 * 浏览器默认录 48k 立体声等于把三倍体积推过一条 2~9 KB/s 的隧道。
 */

/** 参考录音的采样率。跟 CosyVoice 内部 load_wav 的目标一致。 */
export const CLONE_SAMPLE_RATE = 16000;

export type WavResult = {
  /** base64 的 WAV，直接进 POST /api/voices 的 audio 字段 */
  base64: string;
  /** 真实时长（秒），用来在提交前给用户即时反馈 */
  seconds: number;
  /** 播放用的本地 URL；调用方负责 revoke */
  url: string;
  blob: Blob;
};

/** 把音频 blob 解码 → 混单声道 → 重采样到 16k → 编码成 WAV。 */
export async function blobToWav(raw: Blob): Promise<WavResult> {
  const AudioCtx: typeof AudioContext =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

  const buf = await raw.arrayBuffer();
  // 用一个临时 ctx 解码；解码不受 ctx 采样率影响，重采样我们自己做，
  // 因为 OfflineAudioContext 在部分浏览器上对 16k 这种低采样率支持不稳。
  const ctx = new AudioCtx();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(buf.slice(0));
  } finally {
    void ctx.close().catch(() => undefined);
  }

  const mono = mixToMono(decoded);
  const resampled = resampleLinear(mono, decoded.sampleRate, CLONE_SAMPLE_RATE);
  const blob = encodeWav(resampled, CLONE_SAMPLE_RATE);

  return {
    base64: await blobToBase64(blob),
    seconds: resampled.length / CLONE_SAMPLE_RATE,
    url: URL.createObjectURL(blob),
    blob
  };
}

function mixToMono(buffer: AudioBuffer): Float32Array {
  const n = buffer.length;
  if (buffer.numberOfChannels === 1) return buffer.getChannelData(0).slice();
  const out = new Float32Array(n);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < n; i++) out[i] += data[i];
  }
  for (let i = 0; i < n; i++) out[i] /= buffer.numberOfChannels;
  return out;
}

/**
 * 线性插值重采样。
 *
 * 够用而不是最优：参考录音只是给模型提音色特征，48k→16k 的线性插值带来的
 * 混叠在人声频段听不出来，换 sinc 重采样要多几百行且没有可感收益。
 */
function resampleLinear(input: Float32Array, from: number, to: number): Float32Array {
  if (from === to) return input;
  const ratio = from / to;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const a = input[idx] ?? 0;
    const b = input[idx + 1] ?? a;
    out[i] = a + (b - a) * frac;
  }
  return out;
}

/** Float32 [-1,1] → 16bit PCM WAV。 */
function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const bytesPerSample = 2;
  const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
  const view = new DataView(buffer);

  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  const dataBytes = samples.length * bytesPerSample;
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true); // PCM 头长度
  view.setUint16(20, 1, true); // format = PCM
  view.setUint16(22, 1, true); // 单声道
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, "data");
  view.setUint32(40, dataBytes, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    // 先夹再乘，越界的样本会绕回成刺耳的爆音
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += bytesPerSample;
  }
  return new Blob([buffer], { type: "audio/wav" });
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const CHUNK = 8192; // 一次性 apply 整个数组会爆调用栈
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}
